import React, { useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import Router from '@core/routes';
import { UserContextProvider, useUserContext } from '@core/UserContext';
import { FiltersProvider } from '@core/FiltersContext';
import GlobalLoader from '@vms/GlobalLoader';
import AxiosInterceptor from '@core/AxiosInterceptors';
import './App.scss';
import {
    AppId, cssX,
    Env, FCX, getRem,
    GtmInit,
    PlatformContextProvider, usePlatformContext,
} from "@datassential/platform-ui-lib";
import cn from "classnames";
import { Slide, ToastContainer } from "react-toastify";

function AppContent() {
    const { isUserInfoLoaded } = usePlatformContext();

    if (!isUserInfoLoaded) {
        return null;
    }

    return (
        <AxiosInterceptor>
            <div className="App">
                {process.env.NODE_ENV !== 'development' && <GtmInit/>}
                <ToastContainer
                    position="bottom-right"
                    style={{ transform: `translateY(${getRem(-40)})`}}
                    autoClose={3000}
                    hideProgressBar
                    closeOnClick
                    theme="colored"
                    transition={Slide}
                />
                <Router/>
            </div>
        </AxiosInterceptor>
    );
}

const PlatformContextWrapper: FCX = ({ children }) => {
    const { hasAdminAccess } = useUserContext();
    const env = useMemo(
        () => {
            switch ((process.env.REACT_APP_PLATFORM_ENV ?? '').toLowerCase()) {
                case 'qa':
                    return Env.QA;
                case 'staging':
                    return Env.Staging;
                default:
                    return Env.Live;
            }
        },
        []
    );

    const userActions = useMemo(
        () => hasAdminAccess ? [
            {
                id: 'admin',
                content: (
                    <a
                        className={cn(cssX("Dropdown__item"), 'is-tall')}
                        target={'_blank'}
                        rel="noreferrer"
                        href={process.env.REACT_APP_ADMIN}
                    >
                        Tool Admin
                    </a>
                ),
            }
        ] : [],
        [hasAdminAccess]
    );

    return (
        <PlatformContextProvider
            appId={AppId.LaunchesAndRatings}
            env={env}
            enableDevTools={env !== Env.Live}
            additionalUserMenu={userActions}
        >
            {children}
        </PlatformContextProvider>
    )
}

function App() {

    return (
        <GlobalLoader>
            <BrowserRouter>
                <UserContextProvider>
                    <FiltersProvider>
                        <PlatformContextWrapper>
                            <AppContent/>
                        </PlatformContextWrapper>
                    </FiltersProvider>
                </UserContextProvider>
            </BrowserRouter>
        </GlobalLoader>
    );
}

export default App;
